import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import { closePosShift, posError, readTillCounter } from "../../../../services/PosApiServices";
import { num } from "../posHelpers";
import { printCloseSlip, useReceiptHeader } from "./posPrint";
import { amt, day, time, CashDiff, Empty, PageHead, TotalsStats } from "./PosParts";

const same = (a, b) => `${a ?? ""}`.toLowerCase() === `${b ?? ""}`.toLowerCase();

/**
 * POS ▸ Shift Close. Each counter has its own shift: it starts with the
 * counter's first bill and ends here, when the cashier enters the cash counted
 * in the drawer. The next bill on that counter opens the next shift.
 */
const PosShiftClose = ({ branch, userId, state, reload, permit }) => {
  const header = useReceiptHeader();
  const counters = useMemo(() => state?.counters || [], [state]);
  const [counterID, setCounterID] = useState("");
  const [counted, setCounted] = useState("");
  const [remarks, setRemarks] = useState("");
  const [printSlip, setPrintSlip] = useState(true);
  const [busy, setBusy] = useState(false);

  // start on this till's own counter, else the first one with an open shift
  useEffect(() => {
    if (counterID && counters.some((c) => same(c.counterID, counterID))) return;
    const own = counters.find((c) => same(c.counterID, readTillCounter(branch?.guid)));
    const open = counters.find((c) => c.billCount + c.returnCount > 0);
    setCounterID((own || open || counters[0])?.counterID || "");
  }, [counters, counterID, branch]);

  const counter = counters.find((c) => same(c.counterID, counterID)) || null;
  const hasBills = Boolean(counter && counter.billCount + counter.returnCount > 0);
  const expected = num(counter?.cash);
  const difference = num(counted) - expected;

  const closeShift = async () => {
    if (!counter || !hasBills) return;
    if (`${counted}`.trim() === "") {
      toast.error("Enter the cash counted in the drawer");
      return;
    }
    const answer = await Swal.fire({
      title: `Close shift ${counter.shiftNo} on ${counter.counterName}?`,
      html: `Expected cash <b>${amt(expected)}</b>, counted <b>${amt(counted)}</b>.<br/>Bills in a closed shift can no longer be changed.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#4B44D6",
      cancelButtonColor: "#67728A",
      confirmButtonText: "Close shift",
    });
    if (!answer.isConfirmed) return;

    setBusy(true);
    try {
      const closed = await closePosShift({
        branchID: branch.guid,
        counterID: counter.counterID,
        countedCash: num(counted),
        remarks: remarks.trim() || null,
        userID: userId?.id || null,
      });
      toast.success(`Shift ${closed.shiftNo} on ${closed.counterName} closed`);
      if (printSlip) printCloseSlip("shift", closed, header);
      setCounted("");
      setRemarks("");
      await reload();
    } catch (error) {
      toast.error(posError(error, "Could not close the shift"));
      reload();
    } finally {
      setBusy(false);
    }
  };

  const closedToday = state?.closedShifts || [];

  return (
    <>
      <PageHead
        title="Shift Close"
        subtitle={`Business date ${day(state?.transactionDate)} · each counter closes its own shift`}
      >
        <select
          className="form-select"
          value={counterID}
          onChange={(e) => {
            setCounterID(e.target.value);
            setCounted("");
          }}
          aria-label="Counter"
        >
          {counters.map((c) => (
            <option key={c.counterID} value={c.counterID}>
              {c.counterName}
            </option>
          ))}
        </select>
        <button type="button" className="btn btn-light" onClick={reload}>
          Refresh
        </button>
      </PageHead>

      {!counters.length ? (
        <div className="pm-card">
          <Empty>This branch has no counter. Add one in Masters &gt; Counter.</Empty>
        </div>
      ) : (
        <div className="pm-cols">
          <div className="pm-card">
            <h5>
              {counter?.counterName} · Shift {counter?.shiftNo}{" "}
              {hasBills ? (
                <span className="pm-pill pm-pill--accent">Open</span>
              ) : (
                <span className="pm-pill">No bills yet</span>
              )}
            </h5>
            {hasBills ? (
              <p className="text-muted small mb-3">
                First bill {time(counter.firstBillAt)} · last bill {time(counter.lastBillAt)}
              </p>
            ) : null}
            <TotalsStats t={counter} />
          </div>

          <div className="pm-card">
            <h5>Cash count</h5>
            <table className="pm-sum mb-3">
              <tbody>
                <tr>
                  <td className="text-muted small" colSpan={2}>
                    Cash taken on bills, less change given and cash refunds
                  </td>
                </tr>
                <tr className="pm-sum__total">
                  <td>Expected in drawer</td>
                  <td>{amt(expected)}</td>
                </tr>
              </tbody>
            </table>
            <div className="pm-field">
              <label htmlFor="pm-counted">Counted cash</label>
              <input
                id="pm-counted"
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                className="form-control"
                value={counted}
                onChange={(e) => setCounted(e.target.value)}
                disabled={!hasBills}
              />
            </div>
            {`${counted}`.trim() !== "" ? (
              <p className="mb-3">
                Difference: <CashDiff value={difference} />
              </p>
            ) : null}
            <div className="pm-field">
              <label htmlFor="pm-remarks">Remarks</label>
              <input
                id="pm-remarks"
                className="form-control"
                maxLength={250}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                disabled={!hasBills}
              />
            </div>
            <div className="form-check mb-3">
              <input
                id="pm-print"
                type="checkbox"
                className="form-check-input"
                checked={printSlip}
                onChange={(e) => setPrintSlip(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="pm-print">
                Print the shift slip
              </label>
            </div>
            <button
              type="button"
              className="btn btn-pm w-100"
              onClick={closeShift}
              disabled={busy || !hasBills || !permit.add}
              title={permit.add ? "" : "You do not have the right to close a shift"}
            >
              {busy ? "Closing…" : `Close shift ${counter?.shiftNo ?? ""}`}
            </button>
          </div>
        </div>
      )}

      <div className="pm-card">
        <h5>Shifts closed on {day(state?.transactionDate)}</h5>
        {closedToday.length ? (
          <div className="pm-table-wrap">
            <table className="pm-table">
              <thead>
                <tr>
                  <th>Counter</th>
                  <th>Shift</th>
                  <th className="num">Bills</th>
                  <th className="num">Returns</th>
                  <th className="num">Net</th>
                  <th className="num">Expected cash</th>
                  <th className="num">Counted</th>
                  <th className="num">Difference</th>
                  <th>Closed</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {closedToday.map((s) => (
                  <tr key={s.guid}>
                    <td>{s.counterName}</td>
                    <td>{s.shiftNo}</td>
                    <td className="num">{s.billCount}</td>
                    <td className="num">{s.returnCount}</td>
                    <td className="num">{amt(s.netTotal)}</td>
                    <td className="num">{amt(s.expectedCash)}</td>
                    <td className="num">{amt(s.countedCash)}</td>
                    <td className="num">
                      <CashDiff value={s.cashDifference} />
                    </td>
                    <td>
                      {time(s.closedDate)} · {s.closedUserName || "—"}
                    </td>
                    <td className="num">
                      <button type="button" className="btn btn-sm btn-light" onClick={() => printCloseSlip("shift", s, header)}>
                        Print
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty>No shift has been closed on this date yet.</Empty>
        )}
      </div>
    </>
  );
};

PosShiftClose.propTypes = {
  branch: PropTypes.object,
  userId: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  state: PropTypes.object,
  reload: PropTypes.func.isRequired,
  permit: PropTypes.object.isRequired,
};

export default PosShiftClose;
